import { Alert, Box, Collapse, IconButton, Paper, Typography } from "@mui/material"
import { EditOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import { useParams, useSearch } from "@tanstack/react-router"
import type { FC, ReactElement } from "react"
import { useState } from "react"
import { useMutation } from "urql"

import { usePackagingInfo } from "./hooks/usePackagingInfo"
import { PackagingInfoModal } from "./PackagingInfo"
import { type PackagingInfo } from "./types/packaging"
import {
  createDetailListRows,
  findMatchingUomPair,
  transformToFormData,
  validatePackagingWeights,
} from "./utils/packaging"

import DetailList from "@/components/Tables/DetailList"
import type { Mutation, ProductMutationUpdateArgs, ProductUpdateFailure } from "@/graphql/codegen/graphql"
import { UpdateProductMutationDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { useProductActions } from "@/stores/useProductStore"

interface ProductPackageInformationContainerProps {
  isPastProductVersion: boolean
}

export const ProductPackageInformationContainer: FC<ProductPackageInformationContainerProps> = ({
  isPastProductVersion,
}): ReactElement => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [expanded, setExpanded] = useState(true)
  const { enqueueNotification } = useNotificationsStore()
  const { getProduct } = useProductActions()

  const { productId: productIdParam } = useParams({
    from: "/app/product/$productId/edit",
  })

  const { version } = useSearch({ from: "/app/product/$productId/edit/_layout/information" })

  const productId = Number(productIdParam)

  const { data: packagingInfo, fetch: fetchPackagingInfo, loading } = usePackagingInfo({ productId, version })

  const packagingInfoFormValues = transformToFormData(packagingInfo)

  const [, updateProduct] = useMutation<Pick<Mutation, "product">, ProductMutationUpdateArgs>(
    UpdateProductMutationDocument
  )

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const handleSubmit = async (data: PackagingInfo) => {
    const { error, data: result } = await updateProduct({
      input: {
        ...data,
        productId,
        versionNumber: version,
      },
    })

    if (error || (result?.product.update as ProductUpdateFailure)?.error) {
      enqueueNotification({
        type: NotificationType.Error,
        message: "Failed to update the product packaging information",
      })
    } else {
      enqueueNotification({
        type: NotificationType.Success,
        message: "Product packaging information successfully updated",
      })

      await getProduct(productId, version)
      handleModalClose()

      fetchPackagingInfo({ productId, version })
    }
  }

  const hasPackagingWeights = validatePackagingWeights(packagingInfo)
  const detailListRows = createDetailListRows(packagingInfo)
  const canEditPackagingInfo = expanded && !isPastProductVersion

  const hasUomPair = !!findMatchingUomPair(packagingInfo)

  return (
    <Paper className='flex flex-1 flex-col border border-gray-400 p-6' elevation={0}>
      <PackagingInfoModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        defaultValues={packagingInfoFormValues || {}}
        loading={loading}
      />
      <Box className='flex items-start justify-between'>
        <Typography variant='h6'>Packaging Information</Typography>
        <Box className={expanded ? "mb-6" : ""}>
          {canEditPackagingInfo && (
            <IconButton color='primary' aria-label='edit product packaging information' onClick={handleOpenModal}>
              <EditOutlined />
            </IconButton>
          )}
          <IconButton onClick={() => setExpanded(!expanded)} aria-expanded={expanded} aria-label='show more'>
            {expanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        </Box>
      </Box>
      {!hasUomPair && !loading && (
        <Alert severity='error' className='mb-4'>
          US Standard and Metric UoMs must be equivalent. Please update the selections.
        </Alert>
      )}
      {!hasPackagingWeights && !loading && (
        <Alert severity='error' className='mb-4'>
          All UoM Quantity fields must be populated.
        </Alert>
      )}
      <Collapse in={expanded}>
        <DetailList rows={detailListRows} className='mr-10' />
      </Collapse>
    </Paper>
  )
}
